import { useResource, formatDate, titleCase } from "../lib/hooks";
import { useBranding } from "../lib/branding";
import { fileViewUrl } from "../lib/files";

interface IdCardData {
    id: string;
    full_name: string;
    employment_type: string;
    start_date: string;
    ahpra_number: string | null;
    photo_file_id: string | null;
    card_number: string;
    compliant: boolean;
    role_name: string | null;
    /** Card validity tracks the First Aid & CPR expiry — no current first
     *  aid, no valid card, which is exactly the conversation to have. */
    valid_until: string | null;
}

const initials = (name: string): string =>
    name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join("");

/**
 * The digital ID card: who this is, what they do for the organisation, and —
 * the line that matters at an NDIS door — whether every competency their role
 * requires is currently valid. The compliance chip is live, which is the one
 * thing a laminated card can never be.
 *
 * Print uses the standard card ratio (86×54); "Save as PDF" from the print
 * dialog produces a file the worker can keep on their phone.
 */
export const IdCardOverlay = ({ employeeId, onClose }: {
    employeeId: string;
    onClose: () => void;
}) => {
    const branding = useBranding();
    const card = useResource<IdCardData>(`/employees/${employeeId}/id-card`);
    const data = card.data;

    return (
        <div className="print-overlay">
            <div className="print-toolbar no-print">
                <strong>Digital ID card</strong>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn" onClick={() => window.print()}>Print / Save as PDF</button>
                    <button className="btn ghost" onClick={onClose}>Close</button>
                </div>
            </div>

            <div className="print-doc id-card-sheet">
                {card.error && <div className="error-banner">{card.error}</div>}
                {!data && !card.error && <p>Loading…</p>}
                {data && (
                    <div className="id-card">
                        <div className="id-card-band">
                            <img src={branding.logoUrl ?? "/brand/icon-white.png"} alt="" height={26} />
                            <div>
                                <div className="id-card-org">{branding.orgName}</div>
                                <div className="id-card-sub">Digital ID card</div>
                            </div>
                        </div>

                        <div className="id-card-body">
                            <img className="id-card-watermark" src="/brand/icon.png" alt="" aria-hidden="true" />
                            <div className="id-card-photo">
                                {data.photo_file_id ? (
                                    <img src={fileViewUrl(data.photo_file_id)} alt={`Photo of ${data.full_name}`} />
                                ) : (
                                    <span>{initials(data.full_name)}</span>
                                )}
                            </div>
                            <div className="id-card-details">
                                <div className="id-card-name">{data.full_name}</div>
                                <div className="id-card-role">{data.role_name ?? titleCase(data.employment_type)}</div>
                                <dl>
                                    <div><dt>ID</dt><dd className="mono">{data.card_number}</dd></div>
                                    <div><dt>Since</dt><dd>{formatDate(data.start_date)}</dd></div>
                                    {data.ahpra_number && (
                                        <div><dt>AHPRA</dt><dd className="mono">{data.ahpra_number}</dd></div>
                                    )}
                                    <div><dt>Valid to</dt>
                                        <dd className="mono">
                                            {data.valid_until ? formatDate(data.valid_until) : "—"}
                                            {data.valid_until && new Date(data.valid_until) < new Date() && (
                                                <span className="chip block" style={{ marginLeft: 4 }}>expired</span>
                                            )}
                                        </dd>
                                    </div>
                                </dl>
                                <span className={`chip ${data.compliant ? "pass" : "warn"}`}>
                                    {data.compliant ? "✓ Fully compliant" : "Compliance pending"}
                                </span>
                            </div>
                        </div>

                        <div className="id-card-foot">
                            Live card as at {new Date().toLocaleDateString("en-AU")} · valid to the holder's
                            First Aid expiry · Powered by TrustCare
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
